// Global App State Context for Complete Session Persistence
// This context manages ALL navigation and UI state across the entire application

import React, { createContext, useContext, useState, useEffect } from 'react';
import { DEFAULT_MODULE_ID, resolveAvailableModule } from '../constants/moduleAvailability';
import { onBrowserSessionInvalidated } from '../services/browserState';

const AppStateContext = createContext();

export const resolveAuthorisedActiveModule = (activeModule, userModules) => {
  const allowed = (userModules || []).filter(module => module?.name && module.allowed !== false).map(module => module.name);
  if (allowed.includes(activeModule)) return activeModule;
  return allowed[0] || null;
};

// Global app state keys for localStorage
const APP_STATE_KEYS = {
  ACTIVE_MODULE: 'appState_activeModule',
  ACTIVE_CJ_SECTION: 'appState_activeCJSection',
  SIDEBAR_COLLAPSED: 'appState_sidebarCollapsed'
};

export const AppStateProvider = ({ children }) => {
  // Global navigation state with localStorage persistence
  const [activeModule, setActiveModule] = useState(() => {
    return resolveAvailableModule(
      localStorage.getItem(APP_STATE_KEYS.ACTIVE_MODULE) || DEFAULT_MODULE_ID
    );
  });

  const [selectedReport, setSelectedReport] = useState(null);

  const [activeCJSection, setActiveCJSection] = useState(() => {
    return localStorage.getItem(APP_STATE_KEYS.ACTIVE_CJ_SECTION) || 'crownJewels';
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem(APP_STATE_KEYS.SIDEBAR_COLLAPSED) === 'true';
  });

  // Assessment-related state (read-only access for global awareness)
  const [assessmentActiveTab] = useState(0);
  const [assessmentMaturityLevel] = useState('');
  const [assessmentName] = useState('');

  useEffect(() => onBrowserSessionInvalidated(() => {
    setSelectedReport(null);
  }), []);

  // Persist activeModule to localStorage
  useEffect(() => {
    localStorage.setItem(APP_STATE_KEYS.ACTIVE_MODULE, activeModule);
    console.log('🌐 AppState: Persisted activeModule:', activeModule);
  }, [activeModule]);

  // Persist activeCJSection to localStorage
  useEffect(() => {
    localStorage.setItem(APP_STATE_KEYS.ACTIVE_CJ_SECTION, activeCJSection);
    console.log('🌐 AppState: Persisted activeCJSection:', activeCJSection);
  }, [activeCJSection]);

  // Persist sidebarCollapsed to localStorage
  useEffect(() => {
    localStorage.setItem(APP_STATE_KEYS.SIDEBAR_COLLAPSED, sidebarCollapsed.toString());
    console.log('🌐 AppState: Persisted sidebarCollapsed:', sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Enhanced setters that provide logging and consistency
  const setActiveModuleWithLogging = (module) => {
    const resolvedModule = resolveAvailableModule(module);
    console.log('🌐 AppState: Changing activeModule from', activeModule, 'to', resolvedModule);
    setActiveModule(resolvedModule);
  };

  const setSelectedReportWithLogging = (report) => {
    console.log('🌐 AppState: Changing selectedReport to', report);
    setSelectedReport(report);
  };

  const setActiveCJSectionWithLogging = (section) => {
    console.log('🌐 AppState: Changing activeCJSection from', activeCJSection, 'to', section);
    setActiveCJSection(section);
  };

  const setSidebarCollapsedWithLogging = (collapsed) => {
    console.log('🌐 AppState: Changing sidebarCollapsed from', sidebarCollapsed, 'to', collapsed);
    setSidebarCollapsed(collapsed);
  };

  // Function to get complete app state for debugging
  const getCompleteAppState = () => {
    return {
      activeModule,
      selectedReport,
      activeCJSection,
      sidebarCollapsed,
      assessmentActiveTab,
      assessmentMaturityLevel,
      assessmentName
    };
  };

  // Function to reset all app state (for new session/logout)
  const resetAppState = () => {
    console.log('🌐 AppState: Resetting all app state');
    setActiveModule('E8');
    setSelectedReport(null);
    setActiveCJSection('crownJewels');
    setSidebarCollapsed(false);
    
    // Clear localStorage for all app state
    Object.values(APP_STATE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  };

  const value = {
    // Current state
    activeModule,
    selectedReport,
    activeCJSection,
    sidebarCollapsed,
    assessmentActiveTab,
    assessmentMaturityLevel,
    assessmentName,
    
    // State setters
    setActiveModule: setActiveModuleWithLogging,
    setSelectedReport: setSelectedReportWithLogging,
    setActiveCJSection: setActiveCJSectionWithLogging,
    setSidebarCollapsed: setSidebarCollapsedWithLogging,
    
    // Utility functions
    getCompleteAppState,
    resetAppState,
    
    // Constants for external use
    APP_STATE_KEYS
  };

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
};

// Custom hook to use the app state context
export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};

// Hook for debugging app state
export const useAppStateDebug = () => {
  const { getCompleteAppState } = useAppState();
  
  useEffect(() => {
    // Add global debug function to window for easy console access
    window.debugAppState = () => {
      const state = getCompleteAppState();
      console.table(state);
      return state;
    };
    
    console.log('🌐 AppState: Debug function available as window.debugAppState()');
    
    return () => {
      delete window.debugAppState;
    };
  }, [getCompleteAppState]);
};
